import React, { useEffect, useState } from 'react';
import { Users, Clock } from 'lucide-react';
import { useTranslation } from '../i18nContext';
import { api } from '../lib/api';

/**
 * HomeStatsSection — banner under the hero with two key numbers:
 *   • Active members
 *   • Years of trajectory (44 in 2026 etc.)
 *
 * Both values are configured from the admin panel (auto-calculated or fixed)
 * and fetched from `/api/home-stats`.
 */
export default function HomeStatsSection() {
  const t = useTranslation();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/home-stats')
      .then(({ data }) => { if (alive) setStats(data); })
      .catch(() => { if (alive) setStats(null); });
    return () => { alive = false; };
  }, []);

  if (!stats) return null;
  const members = stats.active_members ?? 0;
  const years = stats.years_of_experience ?? 0;
  const isES = t.locale === 'es';

  return (
    <section
      className="bg-white py-12 sm:py-16 border-b border-gray-100"
      data-testid="home-stats-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10">
          <StatCard
            icon={Users}
            value={`${members}+`}
            label={isES ? 'Miembros activos' : 'Active members'}
            sublabel={isES ? 'Intérpretes profesionales en el CMIC' : 'CMIC professional interpreters'}
            testid="home-stats-members"
          />
          <StatCard
            icon={Clock}
            value={String(years)}
            label={isES ? 'Años de trayectoria' : 'Years of experience'}
            sublabel={isES
              ? `Fortaleciendo la interpretación en México desde ${stats.founding_year || 1982}`
              : `Strengthening interpretation in Mexico since ${stats.founding_year || 1982}`}
            testid="home-stats-years"
          />
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, value, label, sublabel, testid }) {
  return (
    <div
      data-testid={testid}
      className="group relative flex items-center gap-5 rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-cmic-50/30 px-6 py-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
    >
      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-cmic-500 to-cmic-700 text-white flex items-center justify-center shadow-md">
        <Icon size={26} strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <p className="text-4xl sm:text-5xl font-bold text-cmic-700 leading-none tabular-nums">{value}</p>
        <p className="mt-1 text-sm font-semibold text-charcoal-800">{label}</p>
        <p className="mt-0.5 text-xs text-charcoal-500 leading-snug">{sublabel}</p>
      </div>
    </div>
  );
}
